import { Voucher } from '../types';
import { scaleToTotalWithRemainder, taytanFormatNumberToString } from '../../../utils/mathUtils';
import { PdfInfoBuilderContext, BuiltPdfInfo } from './types';
import { getCurrencyDetails, formatVoucherDate, formatVoucherDateTime, calcTotalDays, calcAge, resolveBoardBase } from './helpers';
import { roomTypesEnum } from '../../services/constants/roomTypesEnum';

/** Map service type (PG) to preview name for PDF display */
const serviceTypeToPreviewName: Record<string, string> = {
    tours: 'Tour',
    flights: 'Flight',
    guides: 'Guide',
    hotels: 'Hotel',
    generalServices: 'General',
    packages: 'Package',
    visaServices: 'Visa'
};

/**
 * Build voucher detail columns/rows from service data.
 * For flights: shows per-leg routing. For others: shows destination info.
 */
function buildVoucherDetailsFromService(serviceData: any) {
    const serviceType = serviceData.type;

    const flightLegs = serviceData.flightLegs || serviceData.legs;
    if (serviceType === 'flights' && flightLegs?.length) {
        const columns = [
            { title: 'Routing', fieldName: 'destinationRouting' },
            { title: 'Flight', fieldName: 'flightNumber' },
            { title: 'Class', fieldName: 'class' },
            { title: 'Departure', fieldName: 'departureDate' },
            { title: 'CRS/GDS', fieldName: 'crsAndPnr' }
        ];
        const rows = flightLegs.map((leg: any) => {
            // Support both PG populated objects and flat name fields
            const fromName = leg.fromDestination?.iataCode || leg.fromDestination?.name || leg.fromDestinationName || '';
            const toName = leg.toDestination?.iataCode || leg.toDestination?.name || leg.toDestinationName || '';
            return {
                destinationRouting: [fromName, toName].filter(Boolean).join(' - ') || '',
                flightNumber: leg.flightNumber || leg.airline?.code || '',
                class: leg.flightClass || '',
                departureDate: leg.startDate ? formatVoucherDate(leg.startDate) : '',
                crsAndPnr: leg.pnr || ''
            };
        });
        return { voucherDetailsColumns: columns, voucherDetailsRows: rows };
    }

    // Hotel-specific columns: Hotel Name | Check-In | Check-Out | Nights
    if (serviceType === 'hotels' || serviceType === 'packages') {
        const statusRaw = serviceData.status || '';
        const statusLabel = statusRaw === 'OK' ? 'Confirmed' : statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1);

        let nights = '';
        if (serviceData.startDate && serviceData.endDate) {
            const start = new Date(serviceData.startDate);
            const end = new Date(serviceData.endDate);
            if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
                const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                nights = String(diffDays > 0 ? diffDays : 0);
            }
        }

        // Title line: hotel name + address, or package name
        let titleName = '';
        let titleAddress = '';
        if (serviceType === 'hotels') {
            titleName = serviceData.hotelName || serviceData.supplierAccount?.supplierCompanyName || serviceData.name || '';
            const addrParts = [serviceData.hotelAddress, serviceData.hotelCity, serviceData.hotelPostalCode, serviceData.hotelCountry].filter(Boolean);
            titleAddress = addrParts.join(', ');
        } else {
            titleName = serviceData.name || '';
        }

        // Detail lines
        const details: Array<{ label: string; value: string }> = [];
        if (statusLabel) details.push({ label: 'Status:', value: statusLabel });
        if (serviceData.startDate) details.push({ label: 'Check-In:', value: formatVoucherDate(serviceData.startDate) });
        if (serviceData.endDate) details.push({ label: 'Check-Out:', value: formatVoucherDate(serviceData.endDate) });
        if (nights) details.push({ label: 'Nights:', value: nights });

        if (serviceType === 'packages') {
            const passengers: any[] = serviceData.passengers || [];
            const uniqueRooms = new Set(passengers.filter((p: any) => p.roomId).map((p: any) => p.roomId));
            const totalRooms = Number(serviceData.roomsUsed) || uniqueRooms.size;
            if (totalRooms > 0) details.push({ label: 'Total rooms:', value: String(totalRooms) });
        }

        const boardBaseLabel = resolveBoardBase(serviceData.boardBase);
        if (boardBaseLabel) details.push({ label: 'On Base:', value: boardBaseLabel });
        if (serviceData.reservation) details.push({ label: 'Reservation:', value: serviceData.reservation });

        const columns: any[] = [];
        const rows = [{ titleName, titleAddress, details }];
        return { voucherDetailsColumns: columns, voucherDetailsRows: rows };
    }

    // Guide-specific columns
    if (serviceType === 'guides') {
        const columns = [
            { title: 'Service', fieldName: 'service' },
            { title: 'Destination', fieldName: 'destination' },
            { title: 'Status', fieldName: 'status' },
            { title: 'Start Date', fieldName: 'startDate' },
            { title: 'End Date', fieldName: 'endDate' },
            { title: 'Days', fieldName: 'days' }
        ];
        const destinationName = serviceData.destinationName || serviceData.destination?.name || '';
        const statusRaw = serviceData.status || '';
        const totalDays = calcTotalDays(serviceData.startDate, serviceData.endDate);
        const rows = [{
            service: serviceData.name || '',
            destination: destinationName,
            status: statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1),
            startDate: serviceData.startDate ? formatVoucherDate(serviceData.startDate) : '',
            endDate: serviceData.endDate ? formatVoucherDate(serviceData.endDate) : '',
            days: totalDays || ''
        }];
        return { voucherDetailsColumns: columns, voucherDetailsRows: rows };
    }

    // Default columns for other service types (general, visa, package, tour)
    const columns = [
        { title: 'Service', fieldName: 'service', style: { width: '30%' } },
        { title: 'Destination', fieldName: 'destination', style: { width: '30%' } },
        { title: 'Start Date', fieldName: 'startDate', style: { width: '20%' } },
        { title: 'End Date', fieldName: 'endDate', style: { width: '20%' } }
    ];
    const destinationName = serviceData.destinationName || serviceData.destination?.name || '';
    const rows = [{
        service: serviceData.name || '',
        destination: destinationName,
        startDate: serviceData.startDate ? formatVoucherDate(serviceData.startDate) : '',
        endDate: serviceData.endDate ? formatVoucherDate(serviceData.endDate) : ''
    }];
    return { voucherDetailsColumns: columns, voucherDetailsRows: rows };
}

/**
 * Build passenger columns/rows for voucher PDF from service passenger data.
 */
function buildVoucherPassengersFromService(serviceData: any, currencySymbol: string) {
    const serviceType = serviceData.type;
    const passengers: any[] = serviceData.passengers || [];

    // Show active passengers (linked to TF). For flights, include all (may have ticket numbers without TFP link).
    const inServicePassengers = serviceType === 'flights'
        ? passengers
        : passengers.filter((p: any) => p.travelFilePassengerId);

    const titleCol = { title: 'Title', fieldName: 'title', style: { minWidth: '150px', maxWidth: '300px' } };
    // For flights: DOB includes age in parentheses (dd/MM/yy(age)), so no separate Age column
    const birthDateCol = serviceType === 'flights'
        ? { title: 'DOB', fieldName: 'birthDate', style: { minWidth: '70px', maxWidth: '80px' } }
        : { title: 'Birth Date', fieldName: 'birthDate', style: { minWidth: '60px', maxWidth: '60px' } };
    const ageCol = { title: 'Age', fieldName: 'age', style: { minWidth: '30px', maxWidth: '30px' } };
    const priceCols = [
        { title: 'Price', fieldName: 'sellingPriceToShow' },
        { title: 'Tax', fieldName: 'taxToShow' },
        { title: 'Total', fieldName: 'totalToShow' }
    ];

    // For flights: no separate Age column (age shown in DOB column)
    const columns: any[] = serviceType === 'flights'
        ? [titleCol, birthDateCol, ...priceCols]
        : [titleCol, birthDateCol, ageCol, ...priceCols];

    // For flights, add ticket number column
    if (serviceType === 'flights') {
        columns.push({ title: 'Ticket Number', fieldName: 'ticketNumber', style: { minWidth: '80px' } });
    }

    let totalSellingPrice = 0;
    let totalTax = 0;
    let totalTotal = 0;
    // Does any passenger carry money of their own? Drives whether the price
    // columns say anything at all — see the blanking pass below.
    let anyPassengerMoney = false;

    // A service priced only at service level is NOT divided across its
    // passengers. There is no per-passenger figure to print, and splitting the
    // service total evenly states amounts nobody entered — the voucher form
    // does not offer the passenger table for such a service at all.
    //
    // An even split used to run per row, alongside the proportional scaling
    // below, so a service with a MIX of priced and unpriced passengers
    // double-counted: two passengers at 400 each scaled up to 500 + 500 against
    // a 1,000 service total, plus an invented 333.33 for the third — a printed
    // table summing to 1,333.33 on a 1,000 voucher.
    //
    // What remains: the Accounting tab is authoritative for the service totals,
    // and an edit there does not rescale the passenger price components, so the
    // two legitimately disagree. Scale the printed rows proportionally so the
    // passenger table adds up to the voucher's own Amount/Tax. Display only:
    // nothing here is persisted.
    const sumPc = (p: any, key: 'supplierPrice' | 'sellingPrice' | 'tax') =>
        (p.priceComponents || []).reduce(
            (s: number, pc: any) => s + (Number(pc.class?.[key]) || 0),
            0
        );
    // Scaled through the SHARED helper the voucher form uses, so the rows it
    // stores and the rows printed here agree to the cent — including which row
    // absorbs the rounding remainder. Done before the flights re-sort below, so
    // the allocation does not depend on display order.
    const scaledSupplier = scaleToTotalWithRemainder(
        inServicePassengers.map((p: any) => sumPc(p, 'supplierPrice')),
        Number(serviceData.supplierPrice) || 0
    );
    const scaledSelling = scaleToTotalWithRemainder(
        inServicePassengers.map((p: any) => sumPc(p, 'sellingPrice')),
        Number(serviceData.sellingPrice) || 0
    );
    const scaledTax = scaleToTotalWithRemainder(
        inServicePassengers.map((p: any) => sumPc(p, 'tax')),
        Number(serviceData.tax) || 0
    );

    const rows = inServicePassengers.map((p: any, index: number) => {
        // Voucher shows supplier price per passenger (not selling price) — matches TravelBooster
        //
        // A passenger carrying no components of their own prints nothing:
        // scaling zero leaves zero, so the rows sum to the service total rather
        // than overshooting it.
        let supplierPrice = scaledSupplier[index] || 0;
        const tax = scaledTax[index] || 0;

        // A service priced per passenger on the customer side only — components
        // carrying a selling figure with no supplier figure — still has a real
        // per-passenger amount to print, and the voucher's own Amount resolves
        // the same way (`supplierPrice || sellingPrice`, see serviceVoucherProps
        // and voucherPassengersRowsColumn). Summing the supplier side alone
        // printed a table of zeros for it.
        supplierPrice = supplierPrice || scaledSelling[index] || 0;

        const total = supplierPrice + tax;

        totalSellingPrice += supplierPrice;
        totalTax += tax;
        totalTotal += total;
        if (supplierPrice !== 0 || tax !== 0) anyPassengerMoney = true;

        // For flights: Galor format TITLE+LAST/FIRST and DOB as dd/MM/yy(age)
        const passengerName = serviceType === 'flights'
            ? `${p.title || ''}${p.lastName || ''}/${p.firstName || ''}`
            : [p.title, p.firstName, p.lastName].filter(Boolean).join(' ');
        const birthDateDisplay = serviceType === 'flights' && p.birthDate
            ? (() => {
                try {
                    const d = new Date(p.birthDate);
                    if (isNaN(d.getTime())) return formatVoucherDate(p.birthDate);
                    const day = String(d.getDate()).padStart(2, '0');
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const year = String(d.getFullYear()).slice(-2);
                    return `${day}/${month}/${year}(${calcAge(p.birthDate)})`;
                } catch { return formatVoucherDate(p.birthDate); }
            })()
            : (p.birthDate ? formatVoucherDate(p.birthDate) : '');

        const row: any = {
            title: passengerName,
            birthDate: birthDateDisplay,
            age: serviceType === 'flights' ? '' : calcAge(p.birthDate),
            sellingPrice: supplierPrice,
            tax,
            total,
            sellingPriceToShow: `${taytanFormatNumberToString(supplierPrice)}${currencySymbol}`,
            taxToShow: `${taytanFormatNumberToString(tax)}${currencySymbol}`,
            totalToShow: `${taytanFormatNumberToString(total)}${currencySymbol}`,
            _id: p._id,
            _lastName: p.lastName || ''
        };

        if (serviceType === 'flights') {
            row.ticketNumber = p.ticketNumber?.toString() || '';
        }

        return row;
    });

    // Sort flight passengers alphabetically by last name (matching TravelBooster)
    if (serviceType === 'flights') {
        rows.sort((a: any, b: any) => (a._lastName || '').localeCompare(b._lastName || ''));
    }

    // A service priced only at service level has no per-passenger figure, and
    // this table must not invent one. But the LIST still matters — on a flight
    // it is the only place the voucher carries who is travelling and on which
    // ticket, and the airline needs that to issue. So keep the rows and leave
    // the money columns EMPTY rather than printing a 0.00 that reads as a real
    // price. The total box below carries the amount.
    if (!anyPassengerMoney) {
        for (const row of rows) {
            row.sellingPriceToShow = '';
            row.taxToShow = '';
            row.totalToShow = '';
        }
    }

    const summaryLine: any = {
        title: 'Total',
        birthDate: '',
        age: '',
        sellingPriceToShow: anyPassengerMoney
            ? `${taytanFormatNumberToString(totalSellingPrice)}${currencySymbol}`
            : '',
        taxToShow: anyPassengerMoney ? `${taytanFormatNumberToString(totalTax)}${currencySymbol}` : '',
        totalToShow: anyPassengerMoney ? `${taytanFormatNumberToString(totalTotal)}${currencySymbol}` : ''
    };

    return {
        voucherPassengersColumns: columns,
        voucherPassengersRows: rows,
        passengersSummaryLine: summaryLine,
        anyPassengerMoney
    };
}

/**
 * A split voucher (created from Supplier Reconciliation → Split) carries
 * `info.splitFrom` (the parent voucher's id) and, for display, `info.splitFromNumber`
 * (the parent's number). Returns the "Split from Voucher #N" note or undefined.
 */
function buildSplitFromNote(info: any): string | undefined {
    if (!info?.splitFrom) return undefined;
    return `Split from Voucher${info?.splitFromNumber ? ` #${info.splitFromNumber}` : ''}`;
}

/**
 * A split voucher shows a single line for the split portion (`info.amount`,
 * signed) instead of the service's Amount/Commission/Tax/Total block — matching
 * TravelBooster: a positive split is a "Deposit amount", a negative split is
 * "Paid on account".
 */
function buildSplitTotalLine(info: any, currencyCode: string): { label: string; value: string } {
    const amt = Number(info?.amount) || 0;
    return {
        label: amt >= 0 ? 'Deposit amount' : 'Paid on account',
        value: `${taytanFormatNumberToString(amt)} ${currencyCode}`
    };
}

/**
 * Build pdfInfo for Service Voucher
 */
export function buildServiceVoucherPdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext,
    baseInfo: BuiltPdfInfo
): BuiltPdfInfo {
    const info = voucher.info as any;
    const serviceData = context.serviceData;

    if (!serviceData) {
        // Build a basic PDF from stored documentinfo fields (Galor imports / old data without serviceData).
        // These vouchers have supplierName, amounts, dates, and "for" stored in the info record.
        const { code: currencyCode } = getCurrencyDetails(info?.currency, context.currencies);
        const supplierPriceVal = Number(info?.supplierPrice) || 0;
        const tax = Number(info?.tax) || 0;
        const pdfTotalBoxData: Array<{ label: string; value: number | string }> = info?.splitFrom
            ? [buildSplitTotalLine(info, currencyCode)]
            : [
                  { label: 'Amount', value: supplierPriceVal },
                  { label: 'Tax', value: tax },
                  { label: `Total (${currencyCode})`, value: supplierPriceVal + tax }
              ];
        const supplierDetails = info?.supplierDetails
            || (context.supplierAccount ? { address: context.supplierAccount.address, phones: context.supplierAccount.phones } : null);
        const supplierNameOverride = context.supplierAccount?.supplierCompanyName || undefined;
        return {
            ...baseInfo,
            supplierName: supplierNameOverride || info?.supplierName || '',
            supplierDetails,
            pdfTotalBoxData,
            splitFromNote: buildSplitFromNote(info),
            codeStr: info?.for || '',
            voucherDetailsColumns: [],
            voucherDetailsRows: [],
            voucherPassengersColumns: [],
            voucherPassengersRows: [],
            passengersSummaryLine: ''
        };
    }

    const serviceType = serviceData.type;
    const previewName = serviceTypeToPreviewName[serviceType] || '';
    const { code: currencyCode, currency: docCurrency } = getCurrencyDetails(info?.currency, context.currencies);
    const currencySymbol = docCurrency?.symbol || '';

    // Build voucher detail table (destination/routing info)
    const { voucherDetailsColumns, voucherDetailsRows } = buildVoucherDetailsFromService(serviceData);

    // Build passengers table
    const { voucherPassengersColumns, voucherPassengersRows, passengersSummaryLine, anyPassengerMoney } =
        buildVoucherPassengersFromService(serviceData, currencySymbol);

    // Compute passenger totals for fallback (flights have per-passenger pricing, service-level is 0)
    const paxTotals = (serviceData.passengers || []).reduce((acc: any, p: any) => {
        for (const pc of (p.priceComponents || [])) {
            acc.amount += Number(pc.class?.supplierPrice) || 0;
            acc.selling += Number(pc.class?.sellingPrice) || 0;
            acc.commission += Number(pc.class?.commissionAmount) || 0;
            acc.tax += Number(pc.class?.tax) || 0;
        }
        return acc;
    }, { amount: 0, selling: 0, commission: 0, tax: 0 });

    // Voucher total box — fall back to passenger totals when service-level prices are 0.
    // The selling side is part of that chain because the passenger ROWS fall
    // back to it too (see buildVoucherPassengersFromService); without it a
    // service priced per passenger on the customer side only printed a table of
    // real amounts above a box reading 0.00.
    const supplierPriceVal = Number(serviceData.supplierPrice)
        || Number(info?.supplierPrice)
        || paxTotals.amount
        || Number(serviceData.sellingPrice)
        || paxTotals.selling
        || 0;
    const commission = Number(serviceData.commissionAmount) || Number(info?.commission) || paxTotals.commission || 0;
    const tax = Number(serviceData.tax) || Number(info?.tax) || paxTotals.tax || 0;
    const commissionPercent = Number(serviceData.commissionPercent) || (supplierPriceVal ? Math.round((commission / supplierPriceVal) * 10000) / 100 : 0);
    const commissionLabel = commissionPercent ? `Commission (${commissionPercent}%)` : 'Commission';
    let pdfTotalBoxData: Array<{ label: string; value: number | string }>;
    if (info?.splitFrom) {
        pdfTotalBoxData = [buildSplitTotalLine(info, currencyCode)];
    } else {
        pdfTotalBoxData = [
            { label: 'Amount', value: supplierPriceVal },
            { label: commissionLabel, value: commission },
            { label: 'Tax', value: tax },
            { label: `Total (${currencyCode})`, value: supplierPriceVal + tax }
        ];
    }

    // code1code2 from tour-specific data or info.for (fallback for packages etc.)
    let code1code2 = serviceData.code1 || serviceData.code2
        ? { code1: serviceData.code1 || '', code2: serviceData.code2 || '' }
        : undefined;

    // Fallback: use info.for as code display (e.g., packages store trip code in info.for)
    if (!code1code2 && info?.for) {
        code1code2 = { code1: info.for, code2: '' };
    }

    // Build the code string for "Please provide/arrange for:" and "For:" display
    const codeStr = code1code2
        ? `${code1code2.code1 || ''}${code1code2.code2 ? (code1code2.code1 ? '-' : '') + code1code2.code2 : ''}`
        : '';

    // Build pdfBodyBoxData based on service type
    let pdfBodyBoxData: Array<{ label: string; value: string }> = [];
    if (serviceType === 'guides') {
        // Guide voucher: show full service details (matching Galor layout)
        const destinationName = serviceData.destinationName || serviceData.destination?.name || '';
        const statusRaw = serviceData.status || '';
        const statusLabel = statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1);
        const startDateFmt = formatVoucherDateTime(serviceData.startDate);
        const endDateFmt = formatVoucherDateTime(serviceData.endDate);
        const totalDays = calcTotalDays(serviceData.startDate, serviceData.endDate);

        pdfBodyBoxData = [
            { label: 'Service:', value: serviceData.name || info?.serviceName || '' },
            { label: 'Destination:', value: destinationName },
            { label: 'Status:', value: statusLabel }
        ];
        if (serviceData.startDate) {
            pdfBodyBoxData.push({ label: '', value: '' }); // visual separator
            pdfBodyBoxData.push({ label: 'Start Date:', value: startDateFmt });
        }
        if (serviceData.endDate) {
            pdfBodyBoxData.push({ label: 'End Date:', value: endDateFmt });
        }
        if (totalDays) {
            pdfBodyBoxData.push({ label: 'Total days:', value: totalDays });
        }
    } else if (serviceType === 'generalServices') {
        // General service voucher: similar layout to packages (matching Galor)
        const destinationName = serviceData.destinationName || serviceData.destination?.name || '';
        const statusRaw = serviceData.status || '';
        const statusLabel = statusRaw === 'OK' ? 'Confirmed' : statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1);

        pdfBodyBoxData = [
            { label: 'Name:', value: serviceData.name || info?.serviceName || '' },
            { label: 'Destination:', value: destinationName },
            { label: 'Status:', value: statusLabel }
        ];
        if (serviceData.startDate) {
            pdfBodyBoxData.push({ label: '', value: '' }); // visual separator
            pdfBodyBoxData.push({ label: 'Date:', value: formatVoucherDate(serviceData.startDate) });
        }
        if (serviceData.reservation) {
            pdfBodyBoxData.push({ label: 'Reservation No:', value: serviceData.reservation });
        }
    }

    // Use service's supplier account (actual airline/hotel) over document's supplier (may be BSP intermediary)
    const effectiveSupplier = serviceData.supplierAccount || context.supplierAccount;

    // Use stored supplierDetails, or build from effective supplier
    let supplierDetails = info?.supplierDetails
        || (effectiveSupplier ? { address: effectiveSupplier.address, phones: effectiveSupplier.phones } : null);

    // Override supplier name from the actual supplier account
    let supplierNameOverride = effectiveSupplier?.supplierCompanyName || undefined;

    // Build supplier address string from supplier account (for "Our local representative" section)
    let supplierAddressText = '';
    if (effectiveSupplier?.address) {
        const addr = effectiveSupplier.address;
        const phoneStr = (effectiveSupplier.phones || [])
            .map((ph: any) => ph.number).filter(Boolean).join(' / ');
        const parts = [
            addr.address,
            addr.postalCode,
            addr.city,
            phoneStr,
            addr.country?.name
        ].filter(Boolean);
        supplierAddressText = parts.join(' ').replace(/\r?\n/g, ' ').trim();
    }

    // For guide/package vouchers: header shows service name (not supplier), supplier goes to "Details:" at bottom
    let detailsText = '';
    let provideArrangeForText: string | undefined;

    if (serviceType === 'guides' || serviceType === 'packages' || serviceType === 'generalServices') {
        detailsText = effectiveSupplier?.supplierCompanyName || info?.supplierName || '';
        supplierNameOverride = serviceData.name || info?.serviceName || supplierNameOverride;
        supplierDetails = null; // Don't show supplier address/phones in header — shown in "Our local representative" instead
        provideArrangeForText = codeStr || undefined;
    }

    // Build price component rows for packages/general services (matching Galor "Details / Price Component" section)
    // Priority: use service-level price components (servicespricecomponents) if available,
    // otherwise fall back to aggregating from per-passenger data
    let priceComponentRows: Array<{ detail: string; component: string }> | undefined;
    const servicePcs: any[] = serviceData.servicePriceComponents || [];

    if (servicePcs.length > 0) {
        // Build from service-level price components (direct qty + name)
        const grouped = new Map<string, Array<{ qty: number; name: string; fromAge: number; toAge: number }>>();
        for (const spc of servicePcs) {
            const level = spc.id?.componentLevel || 'unknown';
            const name = spc.id?.name || '';
            const qty = Number(spc.class?.qty) || Number(spc.class?.pax) || 0;
            const fromAge = spc.id?.fromAge ?? 0;
            const toAge = spc.id?.toAge ?? 120;
            if (!name) continue;
            if (!grouped.has(level)) grouped.set(level, []);
            grouped.get(level)!.push({ qty, name, fromAge, toAge });
        }
        const rows: Array<{ detail: string; component: string }> = [];
        grouped.forEach((components, _levelId) => {
            // Look up level name from first component's componentLevel metadata
            const levelPc = servicePcs.find((spc: any) => spc.id?.componentLevel === _levelId);
            const levelName = levelPc?.id?.componentLevelName || _levelId;
            components.forEach((c, idx) => {
                rows.push({
                    detail: idx === 0 ? levelName : '',
                    component: `${c.qty} x ${c.name} (${c.fromAge}-${c.toAge})`
                });
            });
        });
        if (rows.length > 0) {
            priceComponentRows = rows;
        }
    } else if (serviceType === 'packages') {
        const paxUsed = Number(serviceData.paxUsed) || 0;
        const boardBaseLabel = resolveBoardBase(serviceData.boardBase);

        // Aggregate "Main" price components from passengers
        const pcCounts = new Map<string, { count: number; fromAge: number; toAge: number }>();
        for (const p of (serviceData.passengers || [])) {
            for (const pc of (p.priceComponents || [])) {
                const name = pc.id?.name || pc.name || '';
                const fromAge = pc.id?.fromAge ?? 0;
                const toAge = pc.id?.toAge ?? 120;
                if (!name) continue;
                const key = `${name}|${fromAge}|${toAge}`;
                const existing = pcCounts.get(key);
                if (existing) existing.count++;
                else pcCounts.set(key, { count: 1, fromAge, toAge });
            }
        }

        const mainRows: Array<{ detail: string; component: string }> = [];
        pcCounts.forEach(({ count, fromAge, toAge }, key) => {
            const name = key.split('|')[0];
            mainRows.push({ detail: mainRows.length === 0 ? 'Main' : '', component: `${count} x ${name} (${fromAge}-${toAge})` });
        });

        if (paxUsed > 0 && boardBaseLabel) {
            priceComponentRows = [
                ...mainRows,
                { detail: 'Board Base', component: `${paxUsed} x ${boardBaseLabel} (0-120)` }
            ];
        } else if (mainRows.length > 0) {
            priceComponentRows = mainRows;
        }
    } else if (serviceType === 'generalServices') {
        const paxUsed = Number(serviceData.paxUsed) || 0;
        if (paxUsed > 0) {
            priceComponentRows = [
                { detail: 'Main', component: `${paxUsed} x Per Adult (12-120)` }
            ];
        }
    }

    // Supplier invoice number from voucher (stored as JSON {number, issueDate, dueDate})
    const supplierInvoiceNumber = (voucher as any).supplierInvoiceNumber
        || (info as any)?.supplierInvoiceNumber
        || ((voucher as any).supplierInvoice as any)?.number
        || (info?.supplierInvoice as any)?.number
        || '';

    // Hotel voucher: build "Details" table + guest list, hide per-passenger prices table
    let hotelDetailsColumns: any[] | undefined;
    let hotelDetailsRows: any[] | undefined;
    let hotelGuestColumns: any[] | undefined;
    let hotelGuestRows: any[] | undefined;
    // Default: passenger table is shown only for flights. Every other
    // service type hides it unless the user explicitly opts in via
    // info.hidePassengersTable === false (e.g. issuing a voucher with
    // "Display all passengers" toggled on).
    const explicitlyHidden = info?.hidePassengersTable === true
        || info?.pdfInfo?.hidePassengersTable === true;
    const explicitlyShown = info?.hidePassengersTable === false
        || info?.pdfInfo?.hidePassengersTable === false;
    // A service priced only on the Accounting tab has nothing per passenger to
    // print as MONEY — the price columns are blanked above rather than showing
    // an invented share. The list itself is a different question: on a flight
    // it carries the names, dates of birth and ticket numbers the airline
    // issues against, and dropping it would strip a voucher of the only record
    // of who is travelling. So the table goes only when it would carry nothing
    // at all — no money and no ticket numbers.
    const anyTicketNumber = voucherPassengersRows.some((row: any) => !!row?.ticketNumber);
    const tableHasContent = anyPassengerMoney || anyTicketNumber;
    let hidePassengersTable: boolean;
    if (explicitlyHidden || !tableHasContent) {
        hidePassengersTable = true;
    } else if (explicitlyShown) {
        hidePassengersTable = false;
    } else {
        hidePassengersTable = serviceType !== 'flights';
    }

    if (serviceType === 'hotels' || serviceType === 'packages') {
        const servicePcsLocal: any[] = serviceData.servicePriceComponents || [];

        // Build price breakdown from PCs
        hotelDetailsColumns = [
            { title: 'Item', fieldName: 'item' },
            { title: 'Qty', fieldName: 'qty' }
        ];

        const pcRows: any[] = [];

        // Get abbreviation code from PC (resolved name or raw string)
        const getAbbrCode = (pc: any) => {
            return pc.id?.abbreviationName || (typeof pc.id?.abbreviation === 'string' && isNaN(Number(pc.id.abbreviation)) ? pc.id.abbreviation : '');
        };

        // Room-type PCs render as "rooms × N passengers" (e.g. "1 × 2
        // passengers"); other PCs (board/supplement) render as just pax.
        const ROOM_RELATED_ABBRS = new Set(['SGLB', 'DBLB', 'EXTBD', 'QUAD']);
        const formatQty = (abbr: string, rooms: number, pax: number): string => {
            if (ROOM_RELATED_ABBRS.has(abbr.toUpperCase()) && rooms > 0) {
                const paxLabel = pax === 1 ? 'passenger' : 'passengers';
                return `${rooms} × ${pax || rooms} ${paxLabel}`;
            }
            const display = rooms > 0 ? rooms : pax;
            return String(display);
        };

        for (const pc of servicePcsLocal) {
            const rooms = Number(pc.class?.rooms) || 0;
            const pax = Number(pc.class?.pax) || 0;
            if (rooms <= 0 && pax <= 0) continue;
            const abbr = getAbbrCode(pc);
            const item = abbr ? `${abbr} - ${pc.id?.name || ''}` : (pc.id?.name || '');
            pcRows.push({ item, qty: formatQty(abbr, rooms, pax) });
        }

        if (pcRows.length > 0) {
            hotelDetailsRows = pcRows;
        } else {
            // Fallback for old data without PCs
            const passengers: any[] = serviceData.passengers || [];
            const activeGuests = passengers.filter((p: any) => p.travelFilePassengerId);
            const uniqueRooms = new Set(activeGuests.filter((p: any) => p.roomId).map((p: any) => p.roomId));
            const totalRooms = Number(serviceData.roomsUsed) || uniqueRooms.size || 0;
            const roomTypeValues = [...new Set(
                activeGuests.filter((p: any) => p.roomType).map((p: any) => roomTypesEnum[p.roomType] || String(p.roomType))
            )];
            hotelDetailsRows = [{
                item: `${roomTypeValues.join(', ')} (${resolveBoardBase(serviceData.boardBase)})`,
                qty: String(totalRooms)
            }];
        }

        // Hotels/packages show the PC breakdown instead of a passenger table by
        // default — but not when the user explicitly ticked "Show Passengers
        // Table in Voucher PDF", which is what `explicitlyShown` records.
        if (!explicitlyShown) {
            hidePassengersTable = true;
        }
    }

    // Passenger count for the hotel voucher header. Only set for hotel/
    // package services so other types don't render a stray field.
    let hotelPassengerCount: number | undefined;
    if (serviceType === 'hotels' || serviceType === 'packages') {
        const passengers: any[] = serviceData.passengers || [];
        hotelPassengerCount = passengers.filter(
            (p: any) => p?.travelFilePassengerId && p?.isInService !== false
        ).length;
    }

    return {
        ...baseInfo,
        travelFileServiceTypePreviewName: previewName,
        supplierDetails,
        supplierNameOverride,
        code1code2,
        voucherDetailsColumns,
        voucherDetailsRows,
        voucherPassengersColumns,
        voucherPassengersRows,
        passengersSummaryLine,
        pdfBodyBoxData,
        pdfTotalBoxData,
        splitFromNote: buildSplitFromNote(info),
        provideArrangeForText,
        detailsText,
        supplierAddressText,
        priceComponentRows,
        hotelDetailsColumns,
        hotelDetailsRows,
        hotelGuestColumns,
        hotelGuestRows,
        hidePassengersTable,
        hotelPassengerCount,
        supplierInvoiceNumber
    };
}

/**
 * Build pdfInfo for Refund (similar to voucher but with additional price columns)
 */
export function buildServiceRefundPdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext,
    baseInfo: BuiltPdfInfo
): BuiltPdfInfo {
    const info = voucher.info as any;
    const serviceData = context.serviceData;

    if (!serviceData) {
        return baseInfo;
    }

    const serviceType = serviceData.type;
    const previewName = serviceTypeToPreviewName[serviceType] || '';
    const { code: currencyCode, currency: docCurrency } = getCurrencyDetails(info?.currency, context.currencies);
    const currencySymbol = docCurrency?.symbol || '';

    // Build voucher detail table
    const { voucherDetailsColumns, voucherDetailsRows } = buildVoucherDetailsFromService(serviceData);

    // Build passengers with clean columns: Name, DOB, Age, Amount, Commission, Tax
    // For refunds, only show selected passengers (stored as refundPassengerIds in documentinfo)
    const allPassengers: any[] = serviceData.passengers || [];
    // Compare as strings — the ids come back from a jsonb column, so they may be
    // numbers while the passenger ids are strings (or vice versa). A raw
    // includes() then matches nothing and the refund prints an empty table.
    const refundPassengerIds = new Set<string>(
        ((info?.refundPassengerIds as any[]) || []).map((id: any) => String(id))
    );
    const selectedPassengers = allPassengers.filter(
        (p: any) => refundPassengerIds.has(String(p._id)) || refundPassengerIds.has(String(p.passengerId))
    );
    // Only narrow the table when the selection actually resolved. An unresolvable
    // selection (deleted passenger, legacy doc) must not blank the table out.
    const passengers = selectedPassengers.length > 0 ? selectedPassengers : allPassengers;

    const voucherPassengersColumns: any[] = [
        { title: 'Name', fieldName: 'name', style: { width: '30%' } },
        { title: 'DOB', fieldName: 'birthDate', style: { width: '12%' } },
        { title: 'Age', fieldName: 'age', style: { width: '8%' } },
        { title: 'Amount', fieldName: 'amountToShow', style: { width: '15%' } },
        { title: 'Commission', fieldName: 'commissionToShow', style: { width: '15%' } },
        { title: 'Tax', fieldName: 'taxToShow', style: { width: '10%' } }
    ];
    if (serviceType === 'flights') {
        voucherPassengersColumns.push({ title: 'Ticket No.', fieldName: 'ticketNumber', style: { width: '15%' } });
    }

    // Authoritative per-passenger refund amounts, when the refund stored them
    // (per-passenger refund — may be partial/edited). Keyed by passenger id.
    const refundDetailById = new Map<string, { amount: number; tax: number }>();
    for (const d of (info?.refundPassengerDetails || [])) {
        const id = String(d?.id ?? '');
        if (id) refundDetailById.set(id, { amount: Number(d?.supplierPrice) || 0, tax: Number(d?.tax) || 0 });
    }
    const hasPerPassengerRefund = refundDetailById.size > 0;

    // The refund amount actually stored on the document (partial OR full). A
    // whole-service (lump-sum) refund has no per-passenger detail, so the PDF must
    // reflect THIS amount rather than re-summing the service's full price components.
    const storedRefundAmount = Number(info?.supplierPrice) || Number(info?.amount) || 0;

    // First pass: full price-component amounts per passenger (display + scaling base).
    const baseRows = passengers.map((p: any) => {
        let paxAmount = 0;
        let paxComm = 0;
        let paxTax = 0;
        // Sum every PC the passenger carries (Airport Tax / supplements / second row).
        for (const pcEntry of (p.priceComponents || [])) {
            const cls = pcEntry?.class || {};
            paxAmount += Number(cls.supplierPrice) || Number(cls.sellingPrice) || 0;
            paxComm += Number(cls.commissionAmount) || 0;
            paxTax += Number(cls.tax) || 0;
        }
        return { p, paxAmount, paxComm, paxTax };
    });
    const fullTotal = baseRows.reduce((s: number, r: any) => s + r.paxAmount, 0);

    // Lump-sum (service) refund: allocate the stored refund amount across passengers so
    // the table and totals show the REFUND, not the full service. Scale by each
    // passenger's share, or split evenly when the service has no per-passenger pricing.
    const isLumpSum =
        !hasPerPassengerRefund && storedRefundAmount > 0 && Math.abs(storedRefundAmount - fullTotal) > 0.001;
    const lumpFactor = isLumpSum && fullTotal > 0 ? storedRefundAmount / fullTotal : 1;
    // A service with no per-passenger pricing is NOT divided across its
    // passengers — the same rule the voucher follows. This used to split the
    // stored refund amount evenly, so a service priced only on the Accounting
    // tab printed per-passenger refunds nobody entered, and contradicted the
    // voucher for that very service, which prints no passenger table at all.
    // With no per-passenger detail there is nothing to itemise, so the refund
    // drops the table too (see `hidePassengersTable` below).
    //
    // Measured on the rows as BUILT, not on the raw price components. The two
    // are independent: on a per-passenger refund the amounts come from what the
    // user typed into the refund dialog (`refundDetailById`), which is stored
    // on the document, so a service with no components at all can still carry
    // real per-passenger figures. Testing `fullTotal` would have thrown that
    // away and printed a bare total. `!== 0` rather than `> 0` so a credit
    // keeps its table too.
    let anyPassengerMoney = false;

    let totalAmount = 0;
    let totalCommission = 0;
    let totalTax = 0;

    const voucherPassengersRows = baseRows.map(({ p, paxAmount, paxComm, paxTax }: any) => {
        const pid = String(p._id ?? p.passengerId ?? '');
        let amt = paxAmount;
        let comm = paxComm;
        let tax = paxTax;
        if (hasPerPassengerRefund && refundDetailById.has(pid)) {
            // Per-passenger refund: use the stored (possibly reduced) amount; scale
            // commission proportionally. A full refund keeps factor = 1 (no change).
            const r = refundDetailById.get(pid)!;
            const factor = paxAmount > 0 ? r.amount / paxAmount : 1;
            amt = r.amount;
            tax = r.tax || paxTax * factor;
            comm = paxComm * factor;
        } else if (lumpFactor !== 1) {
            amt = paxAmount * lumpFactor;
            comm = paxComm * lumpFactor;
            tax = paxTax * lumpFactor;
        }

        totalAmount += amt;
        totalCommission += comm;
        totalTax += tax;
        if (amt !== 0 || tax !== 0) anyPassengerMoney = true;

        const row: any = {
            name: [p.title, p.firstName, p.lastName].filter(Boolean).join(' '),
            birthDate: p.birthDate ? formatVoucherDate(p.birthDate) : '',
            age: calcAge(p.birthDate),
            amountToShow: `${taytanFormatNumberToString(amt)}${currencySymbol}`,
            commissionToShow: `${taytanFormatNumberToString(comm)}${currencySymbol}`,
            taxToShow: `${taytanFormatNumberToString(tax)}${currencySymbol}`,
        };
        if (serviceType === 'flights') {
            row.ticketNumber = p.ticketNumber?.toString() || '';
        }
        return row;
    });

    if (serviceType === 'flights') {
        voucherPassengersRows.sort((a: any, b: any) => (a.ticketNumber || '').localeCompare(b.ticketNumber || ''));
    }

    // Passenger summary line
    const passengersSummaryLine: any = {
        name: `Total Passengers: ${passengers.length}`,
        birthDate: '', age: '',
        amountToShow: `${taytanFormatNumberToString(totalAmount)}${currencySymbol}`,
        commissionToShow: `${taytanFormatNumberToString(totalCommission)}${currencySymbol}`,
        taxToShow: `${taytanFormatNumberToString(totalTax)}${currencySymbol}`,
    };

    // Total box — derived from the (refund-adjusted) passenger rows so it matches the
    // table summary line. Falls back to the stored refund amount when there are no rows.
    const amount = totalAmount || storedRefundAmount || Number(serviceData.supplierPrice) || 0;
    const commission = totalCommission || Number(info?.commission) || Number(serviceData.commissionAmount) || 0;
    const taxAmount = totalTax || Number(info?.tax) || Number(serviceData.tax) || 0;
    // Manual refund fees entered in the Service Refund dialog. They're part of
    // the Total the user approved there, so the PDF has to carry them — but only
    // as rows when non-zero, so ordinary refunds keep the four-row box.
    const processingFee = Number(info?.processingFee) || 0;
    const creditMco = Number(info?.creditMco) || 0;
    const total = amount + taxAmount + processingFee - creditMco;
    const pdfTotalBoxData: Array<{ label: string; value: number | string }> = [
        { label: 'Amount', value: Math.abs(amount) },
        { label: 'Commission', value: Math.abs(commission) },
        { label: 'Tax', value: Math.abs(taxAmount) },
        ...(processingFee ? [{ label: 'Processing Fee', value: Math.abs(processingFee) }] : []),
        ...(creditMco ? [{ label: 'Credit / MCO', value: -Math.abs(creditMco) }] : []),
        { label: `Total (${currencyCode})`, value: Math.abs(total) }
    ];

    const code1code2 = serviceData.code1 || serviceData.code2
        ? { code1: serviceData.code1 || '', code2: serviceData.code2 || '' }
        : undefined;

    // Use service's actual supplier (EL AL) over document's supplier (BSP intermediary)
    const effectiveSupplier = serviceData.supplierAccount || context.supplierAccount;
    const supplierDetails = info?.supplierDetails
        || (effectiveSupplier ? { address: effectiveSupplier.address, phones: effectiveSupplier.phones } : null);
    const supplierNameOverride = effectiveSupplier?.supplierCompanyName || undefined;

    // Reservation number from service
    const reservationNumber = serviceData.reservation || info?.reservationNumber || '';

    return {
        ...baseInfo,
        travelFileServiceTypePreviewName: previewName,
        supplierDetails,
        supplierNameOverride,
        code1code2,
        voucherDetailsColumns,
        voucherDetailsRows,
        voucherPassengersColumns,
        voucherPassengersRows,
        passengersSummaryLine,
        // Nothing per passenger to itemise — see anyPassengerMoney above. As on
        // the voucher, a flight keeps its list even without per-passenger money,
        // because that is where the ticket numbers are.
        hidePassengersTable: !anyPassengerMoney && !voucherPassengersRows.some((r: any) => !!r?.ticketNumber),
        pdfBodyBoxData: [],
        pdfTotalBoxData,
        reservationNumber
    };
}
